import mapboxgl from 'mapbox-gl';

export class MapManager {
    constructor() {
        this.map = null;
        this.spinControl = null;
        this.markers = new Map(); // Store markers by lat,lng
        this.projectMarkers = []; // Store project markers for current city
        this.initialSettings = null;
        this.introSettings = null;
        this.mapStyle = null;
        this.currentCityId = null; // Track current city
    }

    getMarker(lat, lng) {
        const key = `${lat},${lng}`;
        return this.markers.get(key);
    }

    dimOtherMarkers(lat, lng) {
        const currentKey = `${lat},${lng}`;
        this.markers.forEach((marker, key) => {
            if (key !== currentKey) {
                marker.getElement().style.opacity = '0.3';
            }
        });
    }

    resetMarkers() {
        this.markers.forEach(marker => {
            const el = marker.getElement();
            el.classList.remove('pulse');
            el.style.opacity = '1';
        });
    }

    showPulsingMarker(lng, lat, duration = 5000) {
        const marker = this.getMarker(lat, lng);
        if (marker) {
            const el = marker.getElement();
            el.classList.add('pulse');
            this.dimOtherMarkers(lat, lng);

            // Clear existing timeout if any
            if (el._pulseTimeout) {
                clearTimeout(el._pulseTimeout);
            }

            // Set new timeout
            el._pulseTimeout = setTimeout(() => {
                el.classList.remove('pulse');
                this.resetMarkers();
            }, duration);
        }
    }

    showPulsingProjectMarker(marker) {
        if (marker) {
            const el = marker.getElement();
            el.classList.add('pulse');
        }
    }

    resetProjectMarkers() {
        this.projectMarkers.forEach(marker => {
            marker.getElement().classList.remove('pulse');
        });
    }

    createProjectMarker(lat, lng, name) {
        const el = document.createElement('div');
        el.className = 'project-marker';
        el.title = name; // Add tooltip with project name
        
        const marker = new mapboxgl.Marker({
            element: el,
            anchor: 'center'
        }).setLngLat([lng, lat]);

        // Add hover events
        el.addEventListener('mouseenter', () => {
            this.showPulsingProjectMarker(marker);
        });

        el.addEventListener('mouseleave', () => {
            this.resetProjectMarkers();
        });

        return marker;
    }

    hideProjectMarkers() {
        this.projectMarkers.forEach(marker => marker.remove());
        this.projectMarkers = [];
    }

    showProjectMarkers(cityId, projects) {
        console.log('[Map] Showing project markers for city:', cityId, projects);
        
        // Hide any existing project markers
        this.hideProjectMarkers();

        // Ensure projects is an array
        if (!Array.isArray(projects)) {
            console.error('[Map] Invalid projects data:', projects);
            return;
        }

        // Create and show new project markers
        this.projectMarkers = projects.map(project => {
            if (!project.latitude || !project.longitude || !project.project_name) {
                console.warn('[Map] Invalid project data:', project);
                return null;
            }

            const marker = this.createProjectMarker(
                project.latitude,
                project.longitude,
                project.project_name
            );
            marker.addTo(this.map);
            return marker;
        }).filter(Boolean); // Remove any null markers
        
        console.log('[Map] Created project markers:', this.projectMarkers.length);
    }

    async initialize(container, config = {}) {
        // Add Mapbox CSS if not already present
        if (!document.querySelector('link[href*="mapbox-gl.css"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.14.1/mapbox-gl.css';
            document.head.appendChild(link);
        }

        // Set Mapbox token
        if (!config.mapbox_token) {
            console.error('[Map] Missing Mapbox token in config:', config);
            throw new Error('Mapbox token is required');
        }

        try {
            mapboxgl.accessToken = config.mapbox_token;
        } catch (error) {
            console.error('[Map] Error setting Mapbox token:', error);
            throw error;
        }

        // Store settings for transitions
        this.initialSettings = {
            center: config.center,
            zoom: config.zoom,
            pitch: config.pitch
        };

        this.introSettings = config.introAnimation;
        this.mapStyle = config.style;

        // Initialize map with config
        this.map = new mapboxgl.Map({
            container,
            style: this.mapStyle,
            center: config.center || [45, 24],
            zoom: config.zoom || 1.8,
            pitch: config.pitch || 0,
            bearing: config.bearing || 0,
            projection: config.projection || 'globe'
        });

        return new Promise(async (resolve) => {
            this.map.on('load', async () => {
                // Initialize features after map and style are loaded
                this.map.once('style.load', async () => {
                    console.log('[Map] Style loaded');
                    
                    // Setup spinning
                    const { setupSpinning } = await import('../../features/interaction.js');
                    this.spinControl = setupSpinning(this.map);
                });
        
                // Initialize markers
                console.log('[Map] Initializing markers');
                this.initializeLocationMarkers();
                
                // Log marker state
                console.log('[Map] Marker initialization complete. Markers:',
                    Array.from(this.markers.entries()).map(([key, marker]) => ({
                        key,
                        hasMarker: !!marker
                    }))
                );
        
                // Add terrain source and settings
                if (!this.map.getSource('mapbox-dem')) {
                    this.map.addSource('mapbox-dem', {
                        'type': 'raster-dem',
                        'url': 'mapbox://mapbox.terrain-rgb'
                    });
                }
        
                // Set terrain
                this.map.setTerrain({
                    'source': 'mapbox-dem',
                    'exaggeration': 1.5
                });
        
                // Add fog
                this.map.setFog({
                    'color': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        1.8, 'hsl(199, 89.80%, 61.60%)',
                        2.8, 'hsl(199, 89.80%, 61.60%)',
                        6, 'hsl(204, 2.30%, 57.50%)',
                        11, 'hsl(0, 0.00%, 38.00%)'
                    ],
                    'high-color': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        1.8, 'rgb(36, 92, 223)',
                        2.8, 'rgb(36, 92, 223)',
                        11, 'hsl(0, 0.00%, 68.20%)'
                    ],
                    'horizon-blend': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        1.8, 0.02,
                        2.8, 0.1,
                        11, 0.05
                    ],
                    'space-color': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        1.8, 'hsl(240, 46%, 5%)',
                        2.8, 'hsl(240, 46%, 5%)',
                        11, 'hsl(0, 0%, 15%)'
                    ],
                    'star-intensity': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        1.8, 0.15,
                        2.8, 0.1,
                        11, 0
                    ]
                });
        
                // Hide symbol layers initially
                this.map.getStyle().layers.forEach(layer => {
                    if (layer.type === 'symbol') {
                        this.map.setLayoutProperty(layer.id, 'visibility', 'none');
                    }
                });
        
                // Add zoom-based symbol layer visibility
                this.map.on('zoom', () => {
                    const zoom = this.map.getZoom();
                    this.map.getStyle().layers.forEach(layer => {
                        if (layer.type === 'symbol') {
                            this.map.setLayoutProperty(
                                layer.id,
                                'visibility',
                                zoom >= 5 ? 'visible' : 'none'
                            );
                        }
                    });
                });

                console.log('[Map] Map loaded');
                resolve();
            });
        });
    }

    createMarker(lat, lng, iconPath) {
        // Create marker element with icon
        const el = document.createElement('div');
        el.className = 'city-marker';
        el.style.backgroundImage = `url('${iconPath}')`;

        // Create marker
        const marker = new mapboxgl.Marker({
            element: el,
            anchor: 'center'
        }).setLngLat([lng, lat]);

        console.log(`[Map] Created marker at ${lat},${lng}`);
        return marker;
    }

    initializeLocationMarkers() {
        console.log('[Map] Initializing location markers');

        // Clear any existing markers
        this.markers.forEach(marker => marker.remove());
        this.markers.clear();

        // Get all location panels
        const panels = document.querySelectorAll('.panel');
        console.log(`[Map] Found ${panels.length} panels`);
        
        panels.forEach((panel, index) => {
            const lat = parseFloat(panel.dataset.lat);
            const lng = parseFloat(panel.dataset.lng);
            const iconImg = panel.querySelector('img');
            const iconPath = iconImg ? iconImg.src : null;
            
            if (!isNaN(lat) && !isNaN(lng) && iconPath) {
                // Create and add marker
                const marker = this.createMarker(lat, lng, iconPath);
                marker.addTo(this.map);
                
                // Store marker
                const key = `${lat},${lng}`;
                this.markers.set(key, marker);
                
                // Add hover events
                panel.addEventListener('mouseenter', () => {
                    // Add pulse class to hovered marker
                    marker.getElement().classList.add('pulse');
                    
                    // Dim other markers
                    this.markers.forEach((m, k) => {
                        if (k !== key) {
                            m.getElement().classList.add('dimmed');
                        }
                    });
                });
                
                panel.addEventListener('mouseleave', () => {
                    // Remove pulse and dimmed classes
                    marker.getElement().classList.remove('pulse');
                    this.markers.forEach(m => {
                        m.getElement().classList.remove('dimmed');
                    });
                });
            }
        });

        console.log(`[Map] Initialized ${this.markers.size} markers`);
    }

    transitionToLocation(lat, lng, cityId, projects = []) {
        console.log(`[Map] Transitioning to location: ${lat}, ${lng}, cityId: ${cityId}`);
        
        // Stop spinning and set flying state
        if (this.spinControl) {
            this.spinControl.stopSpinning();
            this.spinControl.setFlying(true);
        }

        // Store projects for use after animation
        const projectsToShow = projects;

        // Listen for moveend event
        this.map.once('moveend', () => {
            if (this.spinControl) {
                this.spinControl.setFlying(false);
                setTimeout(() => this.spinControl.startSpinning(), 1000);
            }

            // Show project markers after animation completes
            if (cityId && projectsToShow.length > 0) {
                this.showProjectMarkers(cityId, projectsToShow);
            }
        });

        // Start the map transition with a smooth animation
        this.map.flyTo({
            center: [lng, lat],
            zoom: 11,
            pitch: 45, // Add some pitch for a more dramatic view
            bearing: 0,
            duration: 3000, // Slightly faster transition
            essential: true,
            curve: 1.42, // Ease-in-out curve
            speed: 1.2, // Slightly faster speed
            screenSpeed: 1 // Constant screen speed
        });

        // Show pulsing marker at destination
        this.showPulsingMarker(lng, lat);
    }

    flyToProject(lat, lng) {
        console.log(`[Map] Flying to project: ${lat}, ${lng}`);
        
        // Stop spinning and set flying state
        if (this.spinControl) {
            this.spinControl.stopSpinning();
            this.spinControl.setFlying(true);
        }

        // Listen for moveend event
        this.map.once('moveend', () => {
            if (this.spinControl) {
                this.spinControl.setFlying(false);
                setTimeout(() => this.spinControl.startSpinning(), 1000);
            }
        });

        // Fly to project location with closer zoom
        this.map.flyTo({
            center: [lng, lat],
            zoom: 13, // Closer zoom for project detail
            pitch: 60, // More dramatic pitch for project view
            bearing: 0,
            duration: 2000, // Faster transition for project focus
            essential: true,
            curve: 1.42,
            speed: 1.2,
            screenSpeed: 1
        });

        // Find and pulse the project marker
        const projectMarker = this.projectMarkers.find(marker => {
            const [markerLng, markerLat] = marker.getLngLat().toArray();
            return markerLat === lat && markerLng === lng;
        });

        if (projectMarker) {
            this.showPulsingProjectMarker(projectMarker);
        }
    }

    transitionFromIntro() {
        console.log('[Map] Starting intro transition');
        
        if (!this.map || !this.map.loaded()) {
            console.error('[Map] Map not ready for intro transition');
            return;
        }

        if (this.spinControl) {
            console.log('[Map] Setting flying state');
            this.spinControl.setFlying(true);
        }

        const settings = this.introSettings || this.initialSettings;
        if (!settings) {
            console.error('[Map] No transition settings available');
            return;
        }

        console.log('[Map] Using transition settings:', settings);
        
        // Ensure map is interactive before transition
        this.map.dragPan.enable();
        this.map.scrollZoom.enable();

        this.map.flyTo({
            center: settings.center,
            zoom: settings.zoom,
            pitch: settings.pitch,
            duration: 2000,
            essential: true,
            complete: () => {
                console.log('[Map] Transition complete');
                if (this.spinControl) {
                    this.spinControl.setFlying(false);
                    setTimeout(() => {
                        console.log('[Map] Starting spin');
                        this.spinControl.startSpinning();
                    }, 1000);
                }
            }
        });
    }

    getMap() {
        return this.map;
    }
    
    /**
     * Preserve markers across style changes
     * This method saves all current markers and restores them after a style change
     * Based on the official Mapbox example: https://docs.mapbox.com/mapbox-gl-js/example/style-switch/
     * @param {string} newStyle - The new style URL to apply
     */
    preserveMarkersAcrossStyleChange(newStyle) {
        if (!this.map) {
            console.error('[Map] Map not initialized for style change');
            return;
        }
        
        console.log('[Map] Preserving markers across style change to:', newStyle);
        
        // Store current map state
        const currentZoom = this.map.getZoom();
        const currentCenter = this.map.getCenter();
        const currentPitch = this.map.getPitch();
        const currentBearing = this.map.getBearing();
        
        // Store all current markers
        const savedMarkers = Array.from(this.markers.entries()).map(([key, marker]) => {
            const el = marker.getElement();
            return {
                key,
                lngLat: marker.getLngLat(),
                element: {
                    className: el.className,
                    style: el.style.cssText,
                    backgroundImage: el.style.backgroundImage
                }
            };
        });
        
        // Store all current project markers
        const savedProjectMarkers = this.projectMarkers.map(marker => {
            const el = marker.getElement();
            return {
                lngLat: marker.getLngLat(),
                title: el.title,
                element: {
                    className: el.className,
                    style: el.style.cssText
                }
            };
        });
        
        console.log('[Map] Saved markers:', savedMarkers.length);
        console.log('[Map] Saved project markers:', savedProjectMarkers.length);
        
        // Remove Nira circle BEFORE style change if the function exists
        if (window.removeNiraInteractiveCircle) {
            try {
                window.removeNiraInteractiveCircle();
                console.log('[Map] Removed Nira interactive circle before style change');
            } catch (error) {
                console.error('[Map] Error removing Nira circle:', error);
            }
        }

        // Remove traffic layer BEFORE style change if the function exists
        if (window.completelyRemoveTrafficLayer) {
            try {
                window.completelyRemoveTrafficLayer();
                console.log('[Map] Removed traffic layer before style change');
            } catch (error) {
                console.error('[Map] Error removing traffic layer:', error);
            }
        }
        
        // Remove Health Sites layer BEFORE style change if the function exists
        if (window.completelyRemoveHealthSitesLayer) {
            try {
                window.completelyRemoveHealthSitesLayer();
                console.log('[Map] Removed Health Sites layer before style change');
            } catch (error) {
                console.error('[Map] Error removing Health Sites layer:', error);
            }
        }
        
        // Remove Isochrone layer BEFORE style change if the function exists
        if (window.completelyRemoveIsochroneLayer) {
            try {
                window.completelyRemoveIsochroneLayer();
                console.log('[Map] Removed Isochrone layer before style change');
            } catch (error) {
                console.error('[Map] Error removing Isochrone layer:', error);
            }
        }
        
        // Set up a one-time event handler for the style.load event
        this.map.once('style.load', () => {
            console.log('[Map] Style loaded, restoring state and markers');
            
            // Restore map state
            this.map.setZoom(currentZoom);
            this.map.setCenter(currentCenter);
            this.map.setPitch(currentPitch);
            this.map.setBearing(currentBearing);
            
            // Re-add terrain source if needed
            if (!this.map.getSource('mapbox-dem')) {
                this.map.addSource('mapbox-dem', {
                    'type': 'raster-dem',
                    'url': 'mapbox://mapbox.terrain-rgb'
                });
                
                // Re-apply terrain
                this.map.setTerrain({
                    'source': 'mapbox-dem',
                    'exaggeration': 1.5
                });
                
                // Re-apply fog
                this.map.setFog({
                    'color': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        1.8, 'hsl(199, 89.80%, 61.60%)',
                        2.8, 'hsl(199, 89.80%, 61.60%)',
                        6, 'hsl(204, 2.30%, 57.50%)',
                        11, 'hsl(0, 0.00%, 38.00%)'
                    ],
                    'high-color': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        1.8, 'rgb(36, 92, 223)',
                        2.8, 'rgb(36, 92, 223)',
                        11, 'hsl(0, 0.00%, 68.20%)'
                    ],
                    'horizon-blend': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        1.8, 0.02,
                        2.8, 0.1,
                        11, 0.05
                    ],
                    'space-color': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        1.8, 'hsl(240, 46%, 5%)',
                        2.8, 'hsl(240, 46%, 5%)',
                        11, 'hsl(0, 0%, 15%)'
                    ],
                    'star-intensity': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        1.8, 0.15,
                        2.8, 0.1,
                        11, 0
                    ]
                });
            }
            
            // Clear existing markers
            this.markers.forEach(marker => marker.remove());
            this.markers.clear();
            
            // Clear existing project markers
            this.projectMarkers.forEach(marker => marker.remove());
            this.projectMarkers = [];
            
            // Restore city markers
            savedMarkers.forEach(saved => {
                const el = document.createElement('div');
                el.className = saved.element.className;
                el.style.cssText = saved.element.style;
                el.style.backgroundImage = saved.element.backgroundImage;
                
                const marker = new mapboxgl.Marker({
                    element: el,
                    anchor: 'center'
                }).setLngLat(saved.lngLat);
                
                marker.addTo(this.map);
                this.markers.set(saved.key, marker);
            });
            
            // Restore project markers
            this.projectMarkers = savedProjectMarkers.map(saved => {
                const el = document.createElement('div');
                el.className = saved.element.className;
                el.style.cssText = saved.element.style;
                if (saved.title) el.title = saved.title;
                
                const marker = new mapboxgl.Marker({
                    element: el,
                    anchor: 'center'
                }).setLngLat(saved.lngLat);
                
                marker.addTo(this.map);
                return marker;
            });
            
            console.log('[Map] Restored markers:', this.markers.size);
            console.log('[Map] Restored project markers:', this.projectMarkers.length);
            
            // Re-add Nira circle after style change - with a slight delay to ensure the map is fully ready
            if (window.addNiraInteractiveCircle) {
                try {
                    // Add a small timeout to ensure the map is fully ready
                    setTimeout(() => {
                        window.addNiraInteractiveCircle();
                        console.log('[Map] Re-added Nira interactive circle after style change');
                    }, 100);
                } catch (error) {
                    console.error('[Map] Error re-adding Nira circle:', error);
                }
            }
            
            // Re-add traffic layer after style change if it was active
            if (window.addTrafficLayer && document.querySelector('.traffic-button.active')) {
                try {
                    setTimeout(() => {
                        window.addTrafficLayer();
                        console.log('[Map] Re-added traffic layer after style change');
                    }, 100);
                } catch (error) {
                    console.error('[Map] Error re-adding traffic layer:', error);
                }
            }
            
            // Re-add Health Sites layer after style change if it was active
            if (window.addHealthSitesLayer && document.querySelector('.data-button.active')) {
                try {
                    setTimeout(() => {
                        window.addHealthSitesLayer();
                        console.log('[Map] Re-added Health Sites layer after style change');
                    }, 200);
                } catch (error) {
                    console.error('[Map] Error re-adding Health Sites layer:', error);
                }
            }
            
            // Re-add Isochrone layer if it was active (we'd need to store the last settings)
            if (window.createIsochrone && window.lastIsochroneSettings) {
                try {
                    setTimeout(() => {
                        window.createIsochrone(
                            window.lastIsochroneSettings.coordinates,
                            window.lastIsochroneSettings.options
                        );
                        console.log('[Map] Re-added Isochrone layer after style change');
                    }, 300);
                } catch (error) {
                    console.error('[Map] Error re-adding Isochrone layer:', error);
                }
            }
        });
        
        // Change the style
        this.map.setStyle(newStyle);
    }
}

// Singleton instance
let mapManagerInstance = null;

export const createMapManager = (container, config) => {
    if (!mapManagerInstance) {
        mapManagerInstance = new MapManager();
        
        // Expose to window for debugging
        if (typeof window !== 'undefined') {
            window.mapManager = mapManagerInstance;
            console.log('[Map] Exposed mapManager to window for debugging');
        }
    }
    return mapManagerInstance;
};

export const getMapManager = () => {
    return mapManagerInstance;
};
